package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.domain.model.ClockFlag
import com.evoapps.evoshift.domain.model.KioskSafeguards
import com.evoapps.evoshift.data.dto.workplace.ClockReviewSettingsDto
import com.evoapps.evoshift.data.remote.isPermissionDenied
import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.data.dto.auth.UserDto
import com.evoapps.evoshift.data.dto.roster.ShiftDto
import com.evoapps.evoshift.data.mapper.ShiftMapper
import com.evoapps.evoshift.data.mapper.UserMapper
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.data.remote.IdTokenClaims
import com.evoapps.evoshift.domain.model.ClockAction
import com.evoapps.evoshift.domain.model.KioskClockEvent
import com.evoapps.evoshift.domain.model.KioskClockResult
import com.evoapps.evoshift.domain.model.KioskDevice
import com.evoapps.evoshift.domain.model.KioskException
import com.evoapps.evoshift.domain.model.KioskOfflineException
import com.evoapps.evoshift.domain.model.KioskPairing
import com.evoapps.evoshift.domain.model.KioskPairingCode
import com.evoapps.evoshift.domain.model.KioskPerson
import com.evoapps.evoshift.domain.model.KioskQrCode
import com.evoapps.evoshift.domain.model.KioskSession
import com.evoapps.evoshift.domain.model.KioskTipPool
import com.evoapps.evoshift.data.dto.tips.KioskTipPoolDto
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.KioskRepository
import com.evoapps.evoshift.domain.usecase.kiosk.InMemoryKioskStore
import com.evoapps.evoshift.domain.usecase.kiosk.KioskStore
import dev.gitlive.firebase.auth.FirebaseAuth
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put

/**
 * SH-107 over Firebase: the tablet signs in with the custom token pairKiosk returns, reads its
 * shifts and team from Firestore (firestore.rules let a tablet read just that) and clocks through
 * the kioskClock function; managers and people call the other kiosk functions (functions/kiosk.js).
 */
class KioskRepositoryImpl(
    private val auth: FirebaseAuth,
    private val firestore: AppFirestore,
    private val functions: CloudFunctionsClient,
    private val shiftMapper: ShiftMapper,
    private val userMapper: UserMapper,
    /** The last session, for a restart without network (the token's claims need a refresh then). */
    private val sessionCache: KioskStore = InMemoryKioskStore()
) : KioskRepository {
    private val json = Json { ignoreUnknownKeys = true }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeSession(): Flow<KioskSession?> =
        auth.authStateChanged.map { it?.takeIf { user -> user.uid.isNotBlank() } }.distinctUntilChanged { a, b -> a?.uid == b?.uid }.mapLatest { user ->
            if (user == null || !user.uid.startsWith(KIOSK_UID_PREFIX)) return@mapLatest null
            val claims = runCatching { IdTokenClaims.payload(user.getIdToken(false)) }.getOrNull()
            val session = claims?.let(::sessionOf)
            if (session != null) {
                sessionCache.save(json.encodeToString(CachedSession.serializer(), CachedSession.of(session)))
                return@mapLatest session
            }
            // Offline at start-up: the session this device had, if it is the same tablet.
            sessionCache.load()
                ?.let { runCatching { json.decodeFromString(CachedSession.serializer(), it) }.getOrNull() }
                ?.takeIf { user.uid == KIOSK_UID_PREFIX + it.kioskId }
                ?.toSession()
        }.distinctUntilChanged()

    override suspend fun pair(code: String, name: String?): Result<KioskPairing> = kioskCall {
        val result = functions.call(
            "pairKiosk",
            buildJsonObject {
                put("code", code)
                name?.takeIf { it.isNotBlank() }?.let { put("name", it) }
            },
            requireSignIn = false
        )
        val paired = json.decodeFromJsonElement(PairKioskResult.serializer(), result)
        // Whoever was signed in on this device (a manager pairing their own tablet) is signed out.
        if (auth.currentUser != null) auth.signOut()
        auth.signInWithCustomToken(paired.token)
        KioskPairing(paired.kioskId, paired.workplaceId, paired.workplaceName)
    }

    override suspend fun leave(): Result<Unit> = runCatching {
        sessionCache.save(null)
        auth.signOut()
    }

    override suspend fun verifyExit(userId: String, pin: String): Result<Unit> = kioskCall {
        functions.call("kioskExit", buildJsonObject {
            put("userId", userId)
            put("pin", pin)
        })
        Unit
    }

    override fun observeShifts(workplaceId: String, dates: List<String>): Flow<List<Shift>> {
        if (dates.isEmpty()) return flowOf(emptyList())
        val perDay = dates.map { date ->
            firestore.collection("shifts")
                .where { "rosterId" equalTo RosterIds.of(workplaceId, date.take(4).toInt(), date.substring(5, 7).toInt()) }
                .where { "date" equalTo date }
                .where { "isPublished" equalTo true }
                .safeSnapshots
                .map { snapshot -> snapshot.documents.map { shiftMapper.toDomain(it.data<ShiftDto>()) } }
        }
        return combine(perDay) { days -> days.toList().flatten() }.revokedOnDenial().distinctUntilChanged()
    }

    override fun observeTeam(workplaceId: String): Flow<List<KioskPerson>> =
        firestore.collection("users")
            .where { "engagements.$workplaceId.permission" inArray TEAM_PERMISSIONS }
            .safeSnapshots
            .map { snapshot ->
                snapshot.documents
                    // SH-235: a profile that does not read is left out, not the whole team.
                    .mapNotNull { doc -> runCatching { userMapper.toDomain(doc.data<UserDto>(), emailFromAuth = null) }.getOrNull() }
                    .mapNotNull { user ->
                        val engagement = user.engagements[workplaceId]?.takeIf { it.isActive } ?: return@mapNotNull null
                        KioskPerson(
                            userId = user.uid,
                            displayName = user.displayName,
                            photoUrl = user.photoUrl,
                            isManager = engagement.permission != UserPermission.EMPLOYEE
                        )
                    }
                    .sortedBy { it.displayName.lowercase() }
            }
            .revokedOnDenial()
            .distinctUntilChanged()

    override suspend fun clock(event: KioskClockEvent): Result<KioskClockResult> = kioskCall {
        val result = functions.call(
            "kioskClock",
            buildJsonObject {
                put("userId", event.userId)
                put("pin", event.pin)
                put("eventId", event.eventId)
                event.shiftId?.let { put("shiftId", it) }
                put("action", event.action?.name ?: "AUTO")
                if (event.queued) {
                    put("queued", true)
                    put("deviceTime", event.deviceTime)
                }
            }
        )
        val clocked = json.decodeFromJsonElement(KioskClockResultDto.serializer(), result)
        KioskClockResult(
            action = ClockAction.entries.find { it.name == clocked.action },
            time = clocked.time,
            displayName = clocked.displayName,
            syncedLate = clocked.syncedLate,
            unplanned = clocked.unplanned?.toDomain(),
            flags = clocked.flags.mapNotNull { ClockFlag.fromName(it) },
            photoPath = clocked.photoPath,
            tipShareMinor = clocked.tipShareMinor,
            tipCurrency = clocked.tipCurrency,
            shiftId = clocked.shiftId.ifEmpty { null }
        )
    }

    override suspend fun openTips(managerId: String, pin: String): Result<KioskTipPool> = kioskCall {
        tipPool(managerId, pin, amountMinor = null)
    }

    override suspend fun saveTips(managerId: String, pin: String, amountMinor: Long): Result<KioskTipPool> = kioskCall {
        tipPool(managerId, pin, amountMinor)
    }

    /** SH-124: kioskTipPool; a pool without an amount (none entered yet) comes back with 0. */
    private suspend fun tipPool(managerId: String, pin: String, amountMinor: Long?): KioskTipPool {
        val result = functions.call("kioskTipPool", buildJsonObject {
            put("userId", managerId)
            put("pin", pin)
            amountMinor?.let { put("amountMinor", it) }
        })
        val dto = json.decodeFromJsonElement(KioskTipPoolDto.serializer(), result)
        return KioskTipPool(dto.date, dto.amountMinor ?: 0, dto.currency, dto.shares.map { TipRepositoryImpl.toDomain(it) })
    }

    override suspend fun qrCode(): Result<KioskQrCode?> = kioskCall {
        val result = json.decodeFromJsonElement(QrTokenDto.serializer(), functions.call("kioskQrToken", buildJsonObject { }))
        if (!result.enabled || result.token.isEmpty()) null else KioskQrCode(result.token, result.ttlMs)
    }

    override suspend fun clockWithQr(token: String, action: ClockAction?): Result<KioskClockResult> = kioskCall {
        val result = functions.call("clockWithQr", buildJsonObject {
            put("token", token)
            action?.let { put("action", it.name) }
        })
        val clocked = json.decodeFromJsonElement(QrClockResultDto.serializer(), result)
        KioskClockResult(
            action = ClockAction.entries.find { it.name == clocked.action },
            time = clocked.time,
            displayName = clocked.workplaceName,
            unplanned = clocked.unplanned?.toDomain(),
            flags = clocked.flags.mapNotNull { ClockFlag.fromName(it) },
            shiftId = clocked.shiftId.takeIf { it.isNotEmpty() }
        )
    }

    override fun observeQrEnabled(workplaceId: String): Flow<Boolean> =
        firestore.collection("workplaces").document(workplaceId).safeSnapshots
            .map { doc -> if (doc.exists) doc.data<QrSwitchDto>().settings.kioskQrEnabled else true }
            .distinctUntilChanged()

    override fun observeSafeguards(workplaceId: String): Flow<KioskSafeguards> =
        firestore.collection("workplaces").document(workplaceId).snapshots
            .map { doc ->
                val review = if (doc.exists) doc.data<SafeguardsDto>().settings.clockReview else null
                KioskSafeguards(qrOnly = review?.kioskQrOnly == true, photo = review?.kioskPhoto == true)
            }
            .catch { emit(KioskSafeguards()) }
            .distinctUntilChanged()

    override suspend fun setQrEnabled(workplaceId: String, enabled: Boolean): Result<Unit> = runCatching {
        firestore.collection("workplaces").document(workplaceId).updateFields { "settings.kioskQrEnabled" to enabled }
    }

    override suspend fun createPairingCode(workplaceId: String, name: String?): Result<KioskPairingCode> = kioskCall {
        val result = functions.call("createKioskPairingCode", buildJsonObject {
            put("workplaceId", workplaceId)
            name?.takeIf { it.isNotBlank() }?.let { put("name", it) }
        })
        val code = json.decodeFromJsonElement(PairingCodeDto.serializer(), result)
        KioskPairingCode(code.code, code.expiresAt)
    }

    override fun observeDevices(workplaceId: String): Flow<List<KioskDevice>> =
        firestore.collection("workplaces").document(workplaceId).collection("kiosks")
            .safeSnapshots
            .map { snapshot ->
                snapshot.documents.map { doc ->
                    val d = doc.data<KioskDeviceDto>()
                    KioskDevice(doc.id, d.name, d.createdBy, d.createdAt, d.lastSeenAt, d.revoked)
                }.sortedWith(compareBy({ it.revoked }, { it.name.lowercase() }))
            }
            .distinctUntilChanged()

    override suspend fun revoke(workplaceId: String, kioskId: String): Result<Unit> = kioskCall {
        functions.call("revokeKiosk", buildJsonObject {
            put("workplaceId", workplaceId)
            put("kioskId", kioskId)
        })
        Unit
    }

    override suspend fun pinStatus(): Result<Boolean> = kioskCall {
        val result = functions.call("kioskPinStatus", buildJsonObject { })
        result.jsonObject["isSet"]?.jsonPrimitive?.booleanOrNull == true
    }

    override suspend fun setPin(pin: String?): Result<Unit> = kioskCall {
        functions.call("setKioskPin", buildJsonObject {
            if (pin == null) put("pin", null as String?) else put("pin", pin)
        })
        Unit
    }

    /** The rules stop a tablet once it is unpaired: its listeners then fail with PERMISSION_DENIED. */
    private fun <T> Flow<T>.revokedOnDenial(): Flow<T> = catch { e ->
        if (e.isPermissionDenied() ||
            e.message?.contains("permission-denied", ignoreCase = true) == true
        ) throw KioskException(KioskException.Reason.REVOKED) else throw e
    }

    /** Runs a kiosk call, turning the function's refusals into [KioskException] and no network into [KioskOfflineException]. */
    private suspend fun <T> kioskCall(block: suspend () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        Result.failure(refusalOf(e))
    } catch (e: Exception) {
        // Ktor's connection and timeout failures: the request never reached the function.
        Result.failure(KioskOfflineException(e))
    }

    companion object {
        const val KIOSK_UID_PREFIX = "kiosk_"
        private val TEAM_PERMISSIONS: List<String> = UserPermission.entries.map { it.name } + "MANAGER_OPERATIONAL"

        /** The session from a tablet's token claims; null for a person's token. */
        fun sessionOf(claims: JsonObject): KioskSession? {
            fun str(key: String) = (claims[key] as? JsonPrimitive)?.contentOrNull
            if ((claims["kiosk"] as? JsonPrimitive)?.booleanOrNull != true) return null
            return KioskSession(
                kioskId = str("kioskId") ?: return null,
                workplaceId = str("workplaceId") ?: return null,
                workplaceName = str("workplaceName").orEmpty(),
                timeZone = str("tz")
            )
        }

        /** A function error as the facts the tablet shows; unreachable or failing servers are offline. */
        fun refusalOf(e: CloudFunctionsException): Exception {
            if (e.code in OFFLINE_CODES && e.details == null) return KioskOfflineException(e)
            val details = (e.details as? JsonObject)
            val reason = (details?.get("reason") as? JsonPrimitive)?.contentOrNull
            return KioskException(
                reason = when {
                    reason != null -> KioskException.Reason.of(reason)
                    e.code == "permission-denied" -> KioskException.Reason.REVOKED
                    else -> KioskException.Reason.UNKNOWN
                },
                attemptsLeft = (details?.get("attemptsLeft") as? JsonPrimitive)?.intOrNull,
                retryAfterMinutes = (details?.get("retryAfterMinutes") as? JsonPrimitive)?.intOrNull
            )
        }

        private val OFFLINE_CODES = setOf("unavailable", "deadline-exceeded", "internal")
    }
}

@Serializable
private data class CachedSession(val kioskId: String, val workplaceId: String, val workplaceName: String, val timeZone: String? = null) {
    fun toSession() = KioskSession(kioskId, workplaceId, workplaceName, timeZone)

    companion object {
        fun of(s: KioskSession) = CachedSession(s.kioskId, s.workplaceId, s.workplaceName, s.timeZone)
    }
}

@Serializable
private data class PairKioskResult(val token: String, val kioskId: String, val workplaceId: String, val workplaceName: String = "")

@Serializable
private data class KioskClockResultDto(
    val action: String,
    val time: String,
    val shiftId: String = "",
    val displayName: String = "",
    val syncedLate: Boolean = false,
    val unplanned: UnplannedResultDto? = null,
    val flags: List<String> = emptyList(),
    val photoPath: String? = null,
    val tipShareMinor: Long? = null,
    val tipCurrency: String? = null
)

/** SH-134: only the tablet safeguards of a workplace document. */
@Serializable
private data class SafeguardsDto(val settings: SafeguardsSettingsDto = SafeguardsSettingsDto())

@Serializable
private data class SafeguardsSettingsDto(val clockReview: ClockReviewSettingsDto? = null)

@Serializable
private data class PairingCodeDto(val code: String, val expiresAt: String)

@Serializable
private data class KioskDeviceDto(
    val name: String = "",
    val createdBy: String = "",
    val createdAt: String = "",
    val lastSeenAt: String? = null,
    val revoked: Boolean = false
)

@Serializable
private data class QrTokenDto(val enabled: Boolean = false, val token: String = "", val ttlMs: Long = 20_000)

@Serializable
private data class QrClockResultDto(
    val action: String,
    val time: String,
    val shiftId: String = "",
    val workplaceName: String = "",
    val unplanned: UnplannedResultDto? = null,
    val flags: List<String> = emptyList()
)

/** Only the QR switch of a workplace document (SH-111); a missing field means on. */
@Serializable
private data class QrSwitchDto(val settings: QrSwitchSettingsDto = QrSwitchSettingsDto())

@Serializable
private data class QrSwitchSettingsDto(val kioskQrEnabled: Boolean = true)
